import { cva } from "class-variance-authority";
import { ArrowDownIcon, ArrowUpIcon } from "lucide-react";
import { type KeyboardEvent } from "react";
import { useTranslation } from "../../i18n";
import { cn } from "../../utils";
import { useTableContext } from "./context";
import type { TableColumn } from "./types";

const tableHeaderCellVariants = cva(
  "px-4 text-sm font-medium text-neutral-500 whitespace-nowrap overflow-hidden text-ellipsis flex items-center h-full",
  {
    variants: {
      align: {
        start: "justify-start text-start",
        center: "justify-center text-center",
        end: "justify-end text-end",
      },
    },
    defaultVariants: {
      align: "start",
    },
  },
);

type Props = {
  column: TableColumn;
  className?: string;
};

export function TableHeaderCell({ column, className }: Props) {
  const { t } = useTranslation();
  const { sorting, onSort } = useTableContext();

  const isSorted = sorting?.key === column.key;
  const sortDirection = isSorted ? sorting.direction : null;

  const handleSort = () => {
    if (column.sortable && onSort) {
      onSort(column.sortKey || column.key);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if ((e.key === "Enter" || e.key === " ") && column.sortable && onSort) {
      e.preventDefault();
      onSort(column.key);
    }
  };

  const getAriaSort = () => {
    if (!column.sortable) return undefined;
    if (!isSorted) return "none";
    return sortDirection === "asc" ? "ascending" : "descending";
  };

  const getAriaLabel = () => {
    if (!column.sortable) return undefined;
    if (!isSorted)
      return t("table.sortColumnAriaLabel", { column: column.name });
    if (sortDirection === "asc") {
      return t("table.sortColumnAscAriaLabel", { column: column.name });
    }
    return t("table.sortColumnDescAriaLabel", { column: column.name });
  };

  return (
    <div
      className={cn(
        "min-w-0",
        tableHeaderCellVariants({ align: column.align }),
        column.sortable && [
          "cursor-pointer select-none hover:text-neutral-500",
          "relative focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:z-10",
        ],
        className,
      )}
      style={{
        outlineOffset: column.sortable ? -2 : undefined,
      }}
      onClick={handleSort}
      onKeyDown={handleKeyDown}
      role="columnheader"
      tabIndex={column.sortable ? 0 : undefined}
      aria-sort={getAriaSort()}
      aria-label={getAriaLabel()}
    >
      <div className="flex items-center gap-1">
        <span>{column.name}</span>
        {column.sortable && (
          <div className="flex flex-col">
            {sortDirection === "asc" && (
              <ArrowUpIcon size={14} className="text-neutral-500/50" />
            )}
            {sortDirection === "desc" && (
              <ArrowDownIcon size={14} className="text-neutral-500/50" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
